import { useState, useEffect, ReactNode, ReactElement } from 'react'
import { useQuery } from '@apollo/client'
import { useTranslations } from 'next-intl'
import { getCompassAgreements } from '@/api/agreements'
import { FETCH_PDF_AGREEMENTS } from '@/constants'
import { ICompassAgreementNode } from '@/types/agreements.interface'
import { HardwareInvoiceTypes, InvoiceTypes } from '@/types/billing.interface'

export type ICompassAgreement = {
  isChecked: boolean
  isShown: boolean
  title: InvoiceTypes | HardwareInvoiceTypes
  text: string | ReactNode | ReactElement
  pdfFile: string
  id: string
}

export type ICompassAgreements = {
  hosting: ICompassAgreement
  protection: ICompassAgreement
  hardware: ICompassAgreement
  referral: ICompassAgreement
  refund: ICompassAgreement
  payment_plan: ICompassAgreement
  altcoin: ICompassAgreement
  pending_relocation: ICompassAgreement
  prepaid_credits: ICompassAgreement
  miner_swap: ICompassAgreement
  marketplace: ICompassAgreement
  non_hosted: ICompassAgreement
  indiana_sales_tax_exemption: ICompassAgreement // type of document to fill and upload (has record in database)
  indiana_sales_tax_terms: ICompassAgreement // type of MSA to check to proceed and order (will not have record in database)
  firmware: ICompassAgreement // MSA to accept only if miner has custom firmware
  easy_exit: ICompassAgreement // Agreement for products with prop isEasyExitMiner = true
}

export const useFetchAgreements = () => {
  const t = useTranslations()
  const { data: agreements, loading: loadingAgreements } = useQuery(
    getCompassAgreements,
    {
      skip: parseInt(FETCH_PDF_AGREEMENTS || '0') === 0,
    }
  )
  const [allCompassAgreements, setAllCompassAgreements] =
    useState<ICompassAgreements>({} as ICompassAgreements)

  useEffect(() => {
    if (loadingAgreements) return

    let agreementNodes = []

    // Agreements not fetched from Graphql (but needs to be displayed in certain conditions)
    const additionalAgreements = [
      {
        type: 'ALTCOIN',
        uri: '/pdf/altcoin-agreement.pdf',
      },
      {
        type: 'PENDING_RELOCATION',
        uri: '/pdf/pending-relocation-agreement.pdf',
      },
      {
        type: 'MARKETPLACE',
        uri: '/pdf/Compass_Marketplace_Agreement_03.03.2025.pdf',
      },
      { type: 'REFERRAL', uri: '/pdf/referral-agreement.pdf' },
      {
        type: 'INDIANA_SALES_TAX_TERMS',
        uri: '/pdf/indiana-sales-tax-terms.pdf',
      },
      {
        type: 'EASY_EXIT',
        uri: '/pdf/30_Day_Term_Renewal_Box.pdf',
      },
    ]

    // fallback to local files
    if (parseInt(FETCH_PDF_AGREEMENTS || '0') === 0 || !agreements) {
      agreementNodes = [
        {
          type: 'HOSTING',
          uri: '/pdf/Compass_Retail_Hosting_Service_Agreement_+_Protection_Addendum_February_17th_2025.pdf',
        },
        {
          type: 'PROTECTION',
          uri: '/pdf/Compass_Retail_Hosting_Service_Agreement_+_Protection_Addendum_February_17th_2025.pdf',
        },
        {
          type: 'HARDWARE',
          uri: '/pdf/Compass_Retail_Hardware_Purchase_Agreement_February_17th_2025.pdf',
        },
        {
          type: 'REFUND',
          uri: '/pdf/Refunds-agreement-v1-sep-14.pdf',
        },
        {
          type: 'PAYMENT_PLAN',
          uri: '/pdf/Compass-Hardware-Payment-Plan-Supplement.pdf',
        },
        {
          type: 'PREPAID_CREDITS',
          uri: '/pdf/prepaid-credits-agreement.pdf',
        },
        {
          type: 'MINER_SWAP',
          uri: '/pdf/Unit_Swap_Agreement.pdf',
        },
        {
          type: 'NON_HOSTED',
          uri: '/pdf/Home_Mining_Disclaimer.pdf',
        },
        {
          type: 'INDIANA_SALES_TAX_EXEMPTION',
          uri: '/pdf/indiana-sales-tax-exemption.pdf',
        },
        {
          type: 'FIRMWARE',
          uri: '/pdf/firmware-agreement.pdf',
        },
        ...additionalAgreements,
      ]
    } else {
      agreementNodes = [
        ...agreements?.allCompassAgreements?.nodes,
        ...additionalAgreements,
      ]
    }

    let allAgreements = {} as ICompassAgreements
    agreementNodes.forEach((item: ICompassAgreementNode) => {
      const type = item.type.toLowerCase() as keyof typeof allAgreements
      allAgreements[type] = {
        isShown: type === 'refund' || false,
        isChecked: false,
        title: item.type as InvoiceTypes | HardwareInvoiceTypes,
        text: t(`checkout.${type}`),
        pdfFile: item?.uri,
        id: item?.id || `fallback_${type}`,
      }
    })

    setAllCompassAgreements(allAgreements)
  }, [agreements, loadingAgreements])

  return { allCompassAgreements, loadingAgreements }
}
